'use client'

import posthog from 'posthog-js'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cameraOffset, focusTopInColumn } from '@/app/_components/ground/camera'
import { CAPTIONS } from '@/app/_components/ground/captions'
import { CompleteSpecimen } from '@/app/_components/ground/complete-specimen'
import { DrawSpecimen } from '@/app/_components/ground/draw-specimen'
import type { GroundData } from '@/app/_components/ground/load-ground-data'
import { StandSpecimen } from '@/app/_components/ground/stand-specimen'
import { StudySpecimen } from '@/app/_components/ground/study-specimen'
import { TestSpecimen } from '@/app/_components/ground/test-specimen'
import { GuestDoor } from '@/features/landing/door/guest-door'
import { useDoorPlace } from '@/features/landing/rail/use-hero-door-above'
import { BeatIndex } from '@/features/landing/walk/beat-index'
import { useWalk, viewportHeight } from '@/features/landing/walk/use-walk'
import { exposedAtEnd } from '@/features/landing/walk/walk'
import { cn } from '@/lib/utils'

// Below xl the caption docks as a strip under the rail, with the door
// label as its last row, so the camera's band starts under the strip.
const DOCKED = '(max-width: 1279px)'

// Beat 01's line is the sheet's lip at rest: page 1's top in document
// space, where it sits before any scrolling (spec §3). The walk holds
// beat 01 while page 1 scrolls over it, so the rest state is the only
// moment the study screen is read, and the camera fits the glyph, the
// keyword and the story above the lip.
function restLip(): number {
    const page1 = document.querySelector('[data-walk="page-1"]')
    return page1
        ? page1.getBoundingClientRect().top + window.scrollY
        : viewportHeight()
}

// The ground: the real Learn, sticky under the sheet, advancing through
// six captioned beats as the scroll scrubs (spec §4). The whole ground is
// one door to frame 01 (spec §5): the GuestDoor overlay above the inert
// specimen carries the ink label while the hero's door is wholly on
// screen; whenever the rail holds the door instead — once the hero's
// has scrolled up, and from xl at rest while it is still under the fold
// — the rail door is the one door on screen (spec §6) and the label
// hides — the ground stays a door, unlabelled.
export function GroundStage({ data }: { data: GroundData }) {
    const { beat, t } = useWalk()
    const heroDoor = useDoorPlace('hero-door')
    const heroDoorAbove = heroDoor === 'above'
    const stageRef = useRef<HTMLDivElement>(null)
    const columnRef = useRef<HTMLDivElement>(null)
    const captionRef = useRef<HTMLDivElement>(null)
    const [offset, setOffset] = useState(0)
    const seen = useRef(new Set<number>())

    // Camera (spec §4, §7.2): pan so the active specimen's focus sits in
    // the band the beat can show — under the docked strip below xl, above
    // the line the next page reaches when the scrub completes (for beat
    // 01, the lip at rest). Below xl a specimen may name a wider focus
    // ([data-focus-docked]) for what the strip must not cover (rule 4:
    // the draw beat's keyword); from xl nothing docks, so [data-focus].
    // One measured setState: the offset derives from the DOM, which only
    // a layout effect can read. The focus is measured in the stage's
    // untransformed space, so the current pan (or one mid-transition)
    // never feeds into the next. Keyed on the beat (which swaps the
    // focus), not on t (which scrubs inside it) — and re-measured when
    // the column's DOM settles after the beat: the app's stroke strip
    // enriches its data in an effect (useKanji) and paints a skeleton 30px
    // shorter first, so a specimen measured at mount pans short by that
    // much (the verdict sat under the line on a jump from rest to beat 03).
    useLayoutEffect(() => {
        const stage = stageRef.current
        const col = columnRef.current
        const cap = captionRef.current
        if (!stage || !col || !cap) {
            setOffset(0)
            return
        }
        const measure = () => {
            const docked = window.matchMedia(DOCKED).matches
            const focus =
                (docked
                    ? col.querySelector<HTMLElement>('[data-focus-docked]')
                    : null) ?? col.querySelector<HTMLElement>('[data-focus]')
            if (!focus) {
                setOffset(0)
                return
            }
            const f = focus.getBoundingClientRect()
            const stageTop = stage.getBoundingClientRect().top
            // Below xl the band starts under the strip (whose label row is
            // gone once the hero door is above). From xl nothing docks
            // over the column: the stage's own top.
            const under = docked ? cap.getBoundingClientRect().bottom : stageTop
            const line = exposedAtEnd(beat)
            const floor =
                line !== null
                    ? line * viewportHeight()
                    : beat === 1
                      ? restLip()
                      : viewportHeight()
            setOffset(
                cameraOffset(
                    focusTopInColumn(
                        f.top,
                        col.getBoundingClientRect().top,
                        stageTop
                    ),
                    f.height,
                    under,
                    floor
                )
            )
        }
        measure()
        // Children only: the scrub changes attributes and text, not the
        // tree, so this fires for late layout, not for every frame.
        const settled = new MutationObserver(measure)
        settled.observe(col, { childList: true, subtree: true })
        return () => settled.disconnect()
    }, [beat, heroDoorAbove])

    // Watch-item 2: scroll depth, one event per beat per page view,
    // production-only init makes this a no-op in dev.
    useEffect(() => {
        if (seen.current.has(beat)) return
        seen.current.add(beat)
        try {
            posthog.capture('landing_beat_reached', { beat })
        } catch {
            // analytics must never break the walk
        }
    }, [beat])

    const cap = CAPTIONS[beat]
    const specimen =
        beat === 1 ? (
            <StudySpecimen data={data} />
        ) : beat === 2 ? (
            <DrawSpecimen data={data} t={t} verdict={false} />
        ) : beat === 3 ? (
            <DrawSpecimen data={data} t={1} verdict />
        ) : beat === 4 ? (
            <TestSpecimen
                data={data}
                typed={Math.round(t * data.four.meaning.length)}
            />
        ) : beat === 5 ? (
            <CompleteSpecimen
                data={data}
                revealed={Math.round(t * data.roster.length)}
            />
        ) : (
            <StandSpecimen data={data} />
        )

    return (
        <div
            ref={stageRef}
            className="relative h-full w-full overflow-hidden bg-paper"
        >
            <div
                ref={columnRef}
                className="h-full transition-transform duration-300 ease-out motion-reduce:transition-none"
                style={{
                    transform: offset ? `translateY(${-offset}px)` : undefined,
                }}
            >
                {specimen}
            </div>

            {/* The caption: the six-beat index, title, one sentence. From
                xl: beside the column; below xl (no room beside the column):
                a strip flush under the rail, on paper, in the rail's
                gutters — phones stack the index and the title, tablets set
                them on one row, seated on the index's hairline (on a shared
                baseline the index sat 5px lower than the title and the
                title read high against the underline) — with the ground's
                door label (spec §5) as
                its last row while the hero's door is on screen, and the
                sentence hidden. The strip covers whatever the camera leaves
                behind it (the frame label at rest). The title is a <p>: it
                precedes the page's <h1> and sits in an aria-live region,
                which announces the beat in words (the index is visual). */}
            <div
                ref={captionRef}
                aria-live="polite"
                className="pointer-events-none absolute inset-x-0 top-0 bg-paper xl:inset-x-auto xl:top-12 xl:left-[max(1.5rem,calc((100%-42rem)/2-16rem))] xl:w-60 xl:bg-transparent"
            >
                <div className="border-b border-rule px-5 md:px-10 xl:border-0 xl:px-0">
                    <div className="mx-auto max-w-6xl pt-2.5 pb-2 md:flex md:items-end md:gap-8 md:pt-2 xl:block xl:max-w-none xl:py-0">
                        <BeatIndex beat={beat} t={t} />
                        <p className="mt-2 font-serif text-lp-title-sm text-ink md:mt-0 xl:mt-4 xl:text-lp-title">
                            <span className="sr-only">Beat {beat} of 6. </span>
                            {cap.title}
                        </p>
                        <p className="mt-1.5 hidden font-serif text-lp-copy text-body-fg xl:block">
                            {cap.line}
                        </p>
                    </div>
                </div>
                {!heroDoorAbove ? (
                    <div
                        aria-hidden
                        className="border-b border-rule px-5 py-1.5 text-center font-serif text-lp-caption-sm tracking-lp-22 whitespace-nowrap text-ink uppercase md:px-10 xl:hidden"
                    >
                        Begin at frame 01 — no sign-in
                    </div>
                ) : null}
            </div>

            {/* The ground is one door (spec §5). Ink label at rest, never
                hover-only; it yields to the rail door (spec §6). From xl it
                sits at the page frame's right edge (max-w-6xl, the hero's
                and the rail's), right under where the rail door will rise,
                and shows only while the hero's door is wholly on screen:
                on a short laptop the rail holds the door at rest. Below xl
                the strip above shows it and this one is kept invisible for
                the door's accessible name. */}
            <GuestDoor className="group absolute inset-0 z-20 block w-full cursor-pointer bg-transparent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-vermilion">
                <span
                    className={cn(
                        'absolute top-5 right-[max(2.5rem,calc((100%-72rem)/2))] border border-ink/40 bg-paper px-3 py-1.5 font-serif text-lp-caption-sm tracking-lp-22 whitespace-nowrap text-ink uppercase transition-opacity duration-300 group-hover:border-ink motion-reduce:transition-none max-xl:opacity-0',
                        heroDoor !== 'visible' && 'opacity-0'
                    )}
                >
                    Begin at frame 01 — no sign-in
                </span>
            </GuestDoor>
        </div>
    )
}
